import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { CheckCircle } from "lucide-react"
import type { EmailItem } from "./data"
import { fmtDate, fmtDateLong, prettySender } from "./format"

/**
 * Thread-Ansicht und Verlaufsprotokoll im Mail-Detail.
 *
 * Aus pages/EmailsList.tsx herausgeloest (Schritt G).
 */

export function ThreadTimeline({
  mails, activeId, onSelect,
}: {
  mails: EmailItem[]
  activeId: number
  onSelect: (id: number) => void
}) {
  const [collapsed, setCollapsed] = useState(false)
  return (
    <div style={{
      padding: "10px 20px", borderBottom: "1px solid var(--border-color)",
      background: "var(--bg-subtle, rgba(0,0,0,.02))",
    }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: collapsed ? 0 : 6,
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>
          Thread · {mails.length} E-Mails
        </div>
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "var(--text-muted)" }}
        >
          {collapsed ? "↓ Aufklappen" : "↑ Zuklappen"}
        </button>
      </div>
      {!collapsed && (
        <div style={{ display: "flex", flexDirection: "column", gap: 3, maxHeight: 180, overflowY: "auto" }}>
          {mails.map((m) => {
            const isActive = m.id === activeId
            const isSent = m.email_direction === "sent"
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => onSelect(m.id)}
                style={{
                  padding: "6px 10px", borderRadius: 6,
                  cursor: "pointer", fontSize: 12, textAlign: "left",
                  background: isActive ? "var(--accent-light, rgba(99,102,241,.12))" : "var(--bg-card)",
                  border: isActive ? "1px solid var(--accent-color, #4f46e5)" : "1px solid var(--border-color)",
                  fontWeight: isActive ? 600 : 400,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {isSent ? "→ " : ""}{prettySender(m.sender, m.sender_email)}
                    {/* „gesendet" sagt nicht, WER gesendet hat. Wer im
                        Mailprogramm geantwortet hat, sieht hier warum die Mail
                        trotzdem im Portal steht. */}
                    {/* Der Posteingang oeffnet je Verlauf die neueste Mail, der
                        Plan haengt oft an einer aelteren (44 von 252 Review-
                        Mails, 06.09.2026). Die Auswahl bleibt Abbild des
                        Postfachs — die Zeitleiste sagt nur, WO der Plan liegt. */}
                    {m.plan_offen && (
                      <span
                        title="An dieser Mail haengt ein Agent-Plan, der noch nicht ausgefuehrt wurde"
                        style={{
                          marginLeft: 6, fontSize: 12, fontWeight: 600,
                          color: "#4338ca", background: "#eef2ff",
                          border: "1px solid #c7d2fe",
                          borderRadius: 4, padding: "0 5px", verticalAlign: "middle",
                        }}>
                        Plan offen
                      </span>
                    )}
                    {isSent && m.sent_via && (
                      <span style={{
                        marginLeft: 6, fontSize: 12, fontWeight: 600,
                        color: m.sent_via === "portal" ? "#4338ca" : "#64748b",
                        background: m.sent_via === "portal" ? "#eef2ff" : "#f1f5f9",
                        border: "1px solid " + (m.sent_via === "portal" ? "#c7d2fe" : "#e2e8f0"),
                        borderRadius: 4, padding: "0 5px", verticalAlign: "middle",
                      }}>
                        {m.sent_via === "portal" ? "Portal" : "Mailprogramm"}
                      </span>
                    )}
                  </span>
                  <span style={{ color: "var(--text-muted)", fontSize: 12, whiteSpace: "nowrap" }}>
                    {fmtDate(m.received_at)}
                  </span>
                </div>
                {m.subject && m.subject !== mails[0].subject && (
                  <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 2,
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {m.subject}
                  </div>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export type ThreadHead = { email: EmailItem; threadCount: number; threadMails: EmailItem[] }
export function groupByDay(heads: ThreadHead[]): [string, ThreadHead[]][] {
  const now = new Date(); now.setHours(0, 0, 0, 0)
  const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1)
  const weekAgo = new Date(now); weekAgo.setDate(weekAgo.getDate() - 7)
  const groups: Record<string, ThreadHead[]> = {}
  const order: string[] = []
  for (const h of heads) {
    if (!h.email.received_at) continue
    const d = new Date(h.email.received_at); d.setHours(0, 0, 0, 0)
    let key: string
    if (d.getTime() === now.getTime()) key = "Heute"
    else if (d.getTime() === yesterday.getTime()) key = "Gestern"
    else if (d >= weekAgo) key = "Diese Woche"
    else if (d >= new Date(now.getFullYear(), now.getMonth(), 1)) key = "Dieser Monat"
    else key = d.toLocaleDateString("de-DE", { month: "long", year: "numeric" })
    if (!groups[key]) { groups[key] = []; order.push(key) }
    groups[key].push(h)
  }
  return order.map((k) => [k, groups[k]])
}

/** Action-Bar mit allen Aktionen wie im alten /admin/emails:
 * Status-Toggle, Antworten, Weiterleiten, Verschieben▾, Re-analysieren, Aufgabe, ×
 */
/** Zeigt Projekte des verknuepften HERO-Kontakts inline im E-Mail-Detail,
 *   analog zur Legacy-Portal "HERO Integration"-Box. */
export function ActivityLog({ e, spalte = false }: { e: any; spalte?: boolean }) {
  const actions: string[] = []
  if (e.hero_contact_id) actions.push("Kontakt angelegt")
  if (e.hero_project_id) actions.push("Projekt angelegt")
  if (e.hero_booking_id) actions.push(e.booking_label || "Termin gebucht")
  if (e.email_sent_at) actions.push("Beantwortet")
  if (e.email_forwarded_at) actions.push("Weitergeleitet")
  if (e.status === "done") actions.push("Erledigt")

  // Nichts zu zeigen? → gar nichts rendern, spart Platz
  if (actions.length === 0 && !e.vorgang_id) return null

  // In der Metaspalte steht der Verlauf untereinander statt als Leiste quer
  // ueber die Breite — dort ist er 300px schmal.
  if (spalte) {
    return (
      <div style={{ fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.7 }}>
        {actions.map((a) => (
          <div key={a} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <CheckCircle className="w-3 h-3" style={{ color: "#059669", flexShrink: 0 }} /> {a}
          </div>
        ))}
        {e.vorgang_id && (
          <div style={{ color: "var(--accent-color)", marginTop: 4 }}>Vorgang #{e.vorgang_id}</div>
        )}
      </div>
    )
  }

  return (
    <div style={{
      padding: "8px 20px", borderTop: "1px solid var(--border-color)",
      fontSize: 12, color: "var(--text-muted)",
      display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap",
    }}>
      {actions.length > 0 && (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <CheckCircle className="w-3 h-3" style={{ color: "#059669" }} />
          {actions.join(" · ")}
        </span>
      )}
      {e.vorgang_id && (
        <span style={{ fontSize: 12, color: "var(--accent-color)" }}>
          Vorgang #{e.vorgang_id}
        </span>
      )}
    </div>
  )
}

/** #88: Wer hat was mit dieser Mail gemacht.
 *
 *  Marlene (10.09.2026): „in der Mail im Posteingang sollte an der Seite knapp
 *  ersichtlich sein, wer was zuletzt damit gemacht hat."
 *
 *  Der Block darüber (`ActivityLog`) sagt, WAS an der Mail hängt — abgeleitet
 *  aus ihren eigenen Feldern. Hier steht, WER es getan hat und WANN, aus dem
 *  Aktivitätsprotokoll. Ohne Namen steht „die Plattform" da: was automatisch
 *  passiert ist, soll auch so aussehen.
 */
export function MailVerlauf({ emailId }: { emailId: number }) {
  const q = useQuery({
    queryKey: ["mail-verlauf", emailId],
    queryFn: async () => {
      const r = await fetch(`/api/v1/emails/${emailId}/verlauf`, { credentials: "include" })
      if (!r.ok) return { eintraege: [] as VerlaufEintrag[] }
      return (await r.json()) as { eintraege: VerlaufEintrag[] }
    },
    staleTime: 1000 * 30,
  })

  const eintraege = q.data?.eintraege || []
  if (!eintraege.length) return null

  return (
    <div style={{ fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.7, marginTop: 4 }}>
      {eintraege.map((x) => (
        <div key={x.id} style={{ display: "flex", gap: 6, alignItems: "baseline" }}>
          <span style={{ color: "var(--text-muted)", flexShrink: 0 }}>·</span>
          <span>
            {x.was}
            {" — "}
            <strong style={{ fontWeight: 500 }}>{x.wer || "die Plattform"}</strong>
            {x.wann ? `, ${fmtDateLong(x.wann)}` : ""}
          </span>
        </div>
      ))}
    </div>
  )
}

type VerlaufEintrag = {
  id: number
  was: string
  wer: string | null
  wann: string | null
  severity: string
}

/** HERO-Link-Aktionen: Kontakt aendern / Projekt wechseln / Projekt entfernen / Alles aufheben / Entsperren.
 *   1:1 Paritaet zu admin/emails.py renderHeroSection (Verknuepfungs-Aktionen unten). */
